import {
  Clock3,
  LifeBuoy,
  Mail,
  MessageSquare,
  ReceiptText,
  Send,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { createSupportTicket, getSupportTickets } from '../api.js'
import { useAuth } from '../auth-context.js'
import { NOINDEX_ROBOTS, setPageMetadata } from '../page-metadata.js'

const TICKET_TYPES = [
  ['activation_failed', '开通失败'],
  ['duplicate_payment', '重复付款'],
  ['refund_request', '退款申请'],
  ['feature_bug', '功能异常'],
  ['quota_issue', '额度问题'],
  ['other', '其他'],
]

const STATUS_LABELS = {
  open: '待处理',
  processing: '处理中',
  resolved: '已解决',
  rejected: '已拒绝',
  refunded: '已退款',
}

const FAQS = [
  ['付款后多久开通？', '通常会在 24 小时内人工审核开通，请保留订单号和付款记录。'],
  ['没有开通怎么办？', '提交“开通失败”工单并填写订单号，我们会核对付款与账号信息。'],
  ['可以退款吗？', '可以提交“退款申请”工单，第一版由管理员人工审核和记录，不做自动退款。'],
  ['重复付款怎么办？', '提交“重复付款”或“退款申请”工单，并写明多个订单号。'],
  ['额度没有刷新怎么办？', '选择“额度问题”，说明具体功能、时间和账号状态。'],
]

function errorMessage(error) {
  return error?.message || '请求失败，请稍后重试'
}

function SupportPage() {
  const { user } = useAuth()
  const [form, setForm] = useState({
    type: 'activation_failed',
    orderNo: '',
    contact: user?.email || '',
    title: '',
    description: '',
    screenshotUrl: '',
  })
  const [tickets, setTickets] = useState([])
  const [loadingTickets, setLoadingTickets] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState('')
  const [error, setError] = useState('')

  useEffect(() => setPageMetadata({
    title: '客服与反馈 - 青途 AI',
    description: '提交开通失败、重复付款、退款申请、功能异常和额度问题工单。',
    robots: NOINDEX_ROBOTS,
  }), [])

  useEffect(() => {
    if (!user) return undefined
    const controller = new AbortController()
    setLoadingTickets(true)
    getSupportTickets(controller.signal)
      .then((data) => setTickets(data?.items || []))
      .catch((loadError) => {
        if (loadError.name !== 'AbortError') setError(errorMessage(loadError))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoadingTickets(false)
      })
    return () => controller.abort()
  }, [user])

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }))
  }

  async function handleSubmit(event) {
    event.preventDefault()
    setNotice('')
    setError('')
    if (!user) {
      setError('请先登录后提交客服工单。')
      return
    }
    setSubmitting(true)
    try {
      const result = await createSupportTicket(form)
      const ticket = result?.ticket
      if (ticket) setTickets((current) => [ticket, ...current])
      setForm((current) => ({
        ...current,
        orderNo: '',
        title: '',
        description: '',
        screenshotUrl: '',
      }))
      setNotice('工单已提交，我们通常会在 24 小时内处理。')
    } catch (submitError) {
      setError(errorMessage(submitError))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="page-container support-page">
      <section className="page-heading compact-heading support-heading">
        <div>
          <span className="eyebrow">SUPPORT</span>
          <h1>客服与反馈</h1>
          <p>处理开通失败、重复付款、退款申请、功能异常和额度问题。</p>
        </div>
        <LifeBuoy size={24} />
      </section>

      <section className="support-grid">
        <article className="support-card">
          <header className="support-card-heading">
            <MessageSquare size={20} />
            <div>
              <h2>常见问题</h2>
              <p>提交工单前可以先快速确认处理方式。</p>
            </div>
          </header>
          <div className="support-faq-list">
            {FAQS.map(([question, answer]) => (
              <details key={question}>
                <summary>{question}</summary>
                <p>{answer}</p>
              </details>
            ))}
          </div>
        </article>

        <article className="support-card">
          <header className="support-card-heading">
            <Mail size={20} />
            <div>
              <h2>客服联系方式</h2>
              <p>请尽量提供账号、订单号和问题截图链接。</p>
            </div>
          </header>
          <div className="support-contact-list">
            <p><Mail size={16} /> 客服邮箱：support@kkeutopic.top</p>
            <p><MessageSquare size={16} /> 微信/QQ：可在工单中留下联系方式</p>
            <p><Clock3 size={16} /> 处理时间：通常 24 小时内处理</p>
          </div>
        </article>
      </section>

      <section className="support-grid is-form-grid">
        <article className="support-card support-form-card">
          <header className="support-card-heading">
            <Send size={20} />
            <div>
              <h2>提交反馈工单</h2>
              <p>退款申请也是客服工单的一种类型，由管理员人工处理。</p>
            </div>
          </header>
          {!user && (
            <div className="support-notice">
              请先 <Link to="/login">登录</Link> 后提交工单，方便我们关联你的账号。
            </div>
          )}
          {notice && <div className="admin-notice success" role="status">{notice}</div>}
          {error && <div className="admin-notice error" role="alert">{error}</div>}
          <form className="support-form" onSubmit={handleSubmit}>
            <label>
              <span>问题类型</span>
              <select value={form.type} onChange={(event) => updateField('type', event.target.value)}>
                {TICKET_TYPES.map(([value, label]) => (
                  <option value={value} key={value}>{label}</option>
                ))}
              </select>
            </label>
            <label>
              <span>订单号，可选</span>
              <input value={form.orderNo} onChange={(event) => updateField('orderNo', event.target.value)} placeholder="填写 VIP 订单号或付款备注" />
            </label>
            <label>
              <span>联系方式</span>
              <input required value={form.contact} onChange={(event) => updateField('contact', event.target.value)} placeholder="邮箱、微信或 QQ" />
            </label>
            <label>
              <span>问题标题</span>
              <input required value={form.title} onChange={(event) => updateField('title', event.target.value)} placeholder="例如：付款后 VIP 未开通" />
            </label>
            <label className="is-wide">
              <span>问题描述</span>
              <textarea required rows={5} value={form.description} onChange={(event) => updateField('description', event.target.value)} placeholder="请说明发生时间、操作步骤、订单号和希望处理的结果。" />
            </label>
            <label className="is-wide">
              <span>截图，可选</span>
              <input value={form.screenshotUrl} onChange={(event) => updateField('screenshotUrl', event.target.value)} placeholder="截图链接或网盘地址" />
            </label>
            <button className="primary-button support-submit" type="submit" disabled={submitting || !user}>
              <Send size={16} /> {submitting ? '提交中...' : '提交工单'}
            </button>
          </form>
        </article>

        <article className="support-card">
          <header className="support-card-heading">
            <ReceiptText size={20} />
            <div>
              <h2>我的工单</h2>
              <p>查看最近提交的客服和退款处理记录。</p>
            </div>
          </header>
          {loadingTickets ? (
            <p className="profile-muted">正在加载工单...</p>
          ) : tickets.length === 0 ? (
            <p className="profile-muted">暂无工单。</p>
          ) : (
            <div className="support-ticket-list">
              {tickets.map((ticket) => (
                <article key={ticket.id}>
                  <strong>#{ticket.id} {ticket.title}</strong>
                  <span>{TICKET_TYPES.find(([value]) => value === ticket.type)?.[1] || ticket.type}</span>
                  <small>{STATUS_LABELS[ticket.status] || ticket.status}</small>
                </article>
              ))}
            </div>
          )}
        </article>
      </section>
    </div>
  )
}

export default SupportPage
